"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, MapPin, Phone, PhoneCall, ShieldCheck, Star, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function Stockbridge() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = [{
    text: "Fast 24/7 Response"
  }, {
    text: "Licensed & Insured"
  }, {
    text: "Upfront Pricing"
  }, {
    text: "Stockbridge Local Crews"
  }];
  const quickFeatures = [{
    title: "Burst Pipe & Leak Repair",
    desc: "Rapid response to stop water damage in Stockbridge homes and businesses."
  }, {
    title: "Water Heater Service",
    desc: "Emergency tank & tankless repair, diagnostic, and same-day replacements."
  }, {
    title: "Clogged Drains & Sewers",
    desc: "Heavy-duty clearing and hydro-jetting to restore flow immediately."
  }];
  return <section id="stockbridgehero" data-nav="light" className="relative bg-background text-foreground overflow-hidden py-12 md:py-16 lg:py-20 border-b border-border">
      {/* Decorative subtle background elements */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-primary/5 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column - Main Copy & CTAs */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <MapPin className="size-3.5 text-accent" />
                <span>Serving Stockbridge, GA & Henry County</span>
              </Badge>

              <Badge variant="outline" className="border-accent/40 bg-accent/10 text-accent-foreground px-3 py-1 text-xs font-medium flex items-center gap-1.5">
                <Clock className="size-3.5 text-accent" />
                <span>24/7 Rapid Emergency Dispatch</span>
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-default tracking-tight text-foreground leading-[1.15]">
                Plumbing Services in <span className="text-primary">Stockbridge, GA</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed">
                Professional Plumbing Help Available 24/7 in Stockbridge. From emergency leak repairs to full water heater replacements, our local plumbers are ready to help now.
              </p>
            </div>

            {/* Primary & Secondary Call to Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md text-base font-semibold px-7 h-12 rounded-lg">
                <Link to="/contact/">
                  <Wrench className="size-5" />
                  Request Service
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-accent bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm text-base font-bold px-7 h-12 rounded-lg">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-5" />
                  Call (470) 847-9076
                </a>
              </Button>
            </div>

            {/* Trust points list */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-border/80">
              {trustPoints.map((point, index) => <div key={index} data-index={index} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground font-medium">
                  <CheckCircle2 className="size-4 text-accent shrink-0" />
                  <span>{point.text}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column - Hero Visual Card & Emergency Panel */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-5">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-xl">
              <AspectRatio ratio={16 / 10} className="w-full bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/stockbridge_hero_plumber.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/stockbridge_hero_plumber.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/stockbridge_hero_plumber.png-wvc-sizes" alt="Stockbridge GA emergency plumber fixing pipes" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </AspectRatio>

              {/* Floating Emergency Badge on Image */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <div className="bg-primary/95 text-primary-foreground backdrop-blur-md px-3.5 py-1.5 rounded-md text-xs font-semibold shadow-md flex items-center gap-2 border border-primary-foreground/20">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                  </span>
                  <span>On-Call in Stockbridge</span>
                </div>

                <div className="bg-background/95 text-foreground backdrop-blur-md px-3 py-1.5 rounded-md text-xs font-semibold shadow-md flex items-center gap-1 border border-border">
                  <Star className="size-3.5 fill-accent text-accent" />
                  <span>5.0 Star Local Service</span>
                </div>
              </div>

              {/* Card Footer Details */}
              <div className="p-5 bg-card text-card-foreground border-t border-border flex flex-col gap-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 font-semibold text-foreground">
                    <ShieldCheck className="size-4 text-primary" />
                    <span>Guaranteed Workmanship</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Prompt Arrival</span>
                </div>

                <div className="text-xs text-muted-foreground leading-relaxed">
                  Fast response units dispatched throughout Stockbridge including Eagles Landing, Hudson Bridge Rd, and Downtown Stockbridge.
                </div>
              </div>
            </div>

            {/* Quick Service Highlights Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {quickFeatures.map((item, i) => <Card key={i} data-index={i} className="bg-card text-card-foreground border border-border shadow-xs p-3 rounded-lg flex flex-col gap-1 justify-center">
                  <CardContent className="p-0">
                    <h3 className="text-xs font-bold text-foreground line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="text-[11px] text-muted-foreground leading-tight line-clamp-2 mt-0.5">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

          </motion.div>
        </div>
      </div>
    </section>;
}